/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import Tooltip, { TooltipProps } from '@mui/material/Tooltip';
import { ReactComponent as ArrowDown } from '~/shared/assets/arrowDown.svg';
import { hex2rgba } from '../../../../utils/colors/hex2rgba';
import { FilterItemProps } from '../types';
import { AlertBar } from '~/shared/components';

const DROPDOWN_BORDER_COLOR_INACTIVE = '#99a3af';
const DROPDOWN_BORDER_COLOR_ACTIVE = 'var(--filter-bar-dropdown-border-active)';
const DROPDOWN_BORDER_WIDTH = '1px';
const MAX_MULTI_SELECT_OPTIONS = 10;

const DEFAULT_TOOLTIP_CFG = {
  color: '#ffffff',
  backgroundColor: '#1f2a37',
  opacity: 0.95,
  borderColor: 'transparent',
  borderStyle: 'solid',
  borderWidth: '0px',
  borderRadius: '4px',
  padding: '12px',
  maxWidth: '280px',
  headerFontSize: '14px',
  headerFontWeight: 600,
  headerMarginBottom: '8px',
  contentFontSize: '12px',
  contentFontWeight: 400,
  letterSpacing: 0,
} satisfies Record<string, string | number>;

const normalizeTooltipCfg = (tooltipCfg?: any) => ({
  ...DEFAULT_TOOLTIP_CFG,
  ...(tooltipCfg ?? {}),
});

const SelectContainer = styled(FormControl)`
  min-width: 167px;
  border-radius: 4px 4px 0 0;
  margin-top: 0px !important;
  margin-top: -18px !important;
  .MuiAutocomplete-root {
    border: none;
  }
`;

const StyledAutocomplete = styled(Autocomplete)<{ isopen: number }>`
  border-radius: 5px;
  background: white !important;
  margin-top: 13px;
  margin-right: 12px;
  height: 40px;
  font-size: 13px;
  color: #4c5e72;
  font-weight: 400;
  max-width: 154px;
  width: 154px;
  & .MuiAutocomplete-inputRoot {
    padding: 0 !important;
    overflow: hidden;
  }
  & .MuiOutlinedInput-root {
    padding: 0 !important;
    height: 40px;
    min-height: 40px;
    background: transparent;
    border-radius: 5px;
    border: ${DROPDOWN_BORDER_WIDTH} solid
      ${({ isopen }) => (isopen ? DROPDOWN_BORDER_COLOR_ACTIVE : DROPDOWN_BORDER_COLOR_INACTIVE)};
    border-bottom: ${({ isopen }) => (isopen ? 'none' : undefined)};
    border-bottom-left-radius: ${({ isopen }) => (isopen ? 0 : '5px')};
    border-bottom-right-radius: ${({ isopen }) => (isopen ? 0 : '5px')};
  }
  & .MuiOutlinedInput-root:hover {
    border-color: ${DROPDOWN_BORDER_COLOR_ACTIVE};
  }
  & .MuiOutlinedInput-notchedOutline {
    display: none;
  }
  & .MuiOutlinedInput-root.Mui-focused {
    border: ${DROPDOWN_BORDER_WIDTH} solid
      ${({ isopen }) => (isopen ? DROPDOWN_BORDER_COLOR_ACTIVE : DROPDOWN_BORDER_COLOR_INACTIVE)};
    box-shadow: none;
  }
  & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    display: none;
  }
  & .MuiOutlinedInput-root .MuiAutocomplete-input {
    padding: 5px 4px !important;
    font-size: 15px !important;
    font-weight: 400 !important;
    color: #33485e !important;
  }
  .MuiInputLabel-animated {
    font-size: 13px;
    transform: translate(-4px, -16px);
    opacity: 0;
  }
  .MuiInputLabel-shrink {
    opacity: 1;
    font-size: 13px;
    transform: translate(-4px, -16px);
    padding: 0 4px 3px;
    color: #4c5e72 !important;
    background: #f5f5f5;
    line-height: 1em;
  }
  label + .MuiInput-formControl {
    margin-left: 12px;
    width: calc(100% - 16px);
  }
  .MuiIconButton-root {
    max-width: 20px !important;
    max-height: 20px !important;
  }
  .MuiAutocomplete-popupIndicator {
    padding: 8px 2px 2px !important;
  }
  .MuiAutocomplete-endAdornment {
    top: calc(50% - 3px) !important;
    right: 9px !important;
  }
  .MuiAutocomplete-option {
    padding: 6px 16px !important;
  }
  .MuiAutocomplete-input:first-child {
    color: #33485e !important;
    padding: 5px 4px !important;
    font-size: 15px !important;
    font-weight: 400 !important;
    margin-left: 12px !important;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: calc(100% - 36px) !important;
    width: calc(100% - 36px) !important;
  }
  border: 1px solid ${({ isopen }) => (isopen ? DROPDOWN_BORDER_COLOR_ACTIVE : DROPDOWN_BORDER_COLOR_INACTIVE)};
  border-bottom: ${({ isopen }) => (isopen ? 'none' : undefined)};
  border-bottom-left-radius: ${({ isopen }) => (isopen ? 0 : '5px')};
  border-bottom-right-radius: ${({ isopen }) => (isopen ? 0 : '5px')};
  & .MuiAutocomplete-tag {
    background: transparent;
    border: none;
    margin: 0;
    padding: 0;
    max-width: calc(100% - 36px);
  }
  & .MuiAutocomplete-tag > span {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 15px;
    font-weight: 400;
    color: #33485e;
    padding: 5px 4px !important;
    margin-left: 12px;
  }
`;

type StyledTooltipProps = TooltipProps & {
  $tooltipCfg: any;
};

const StyledTooltipBase = React.forwardRef<HTMLDivElement, StyledTooltipProps>((props, ref) => {
  const { $tooltipCfg, className, ...tooltipProps } = props;

  return <Tooltip ref={ref} classes={{ popper: className, tooltip: 'tooltip', arrow: 'arrow' }} {...tooltipProps} />;
});

StyledTooltipBase.displayName = 'StyledTooltipBase';

const StyledTooltip = styled(StyledTooltipBase)`
  & .tooltip {
    color: ${({ $tooltipCfg }: StyledTooltipProps) => $tooltipCfg.color};
    background-color: ${({ $tooltipCfg }: StyledTooltipProps) =>
      hex2rgba($tooltipCfg.backgroundColor, $tooltipCfg.opacity)};
    border: ${({ $tooltipCfg }: StyledTooltipProps) =>
      `${$tooltipCfg.borderWidth} ${$tooltipCfg.borderStyle} ${hex2rgba($tooltipCfg.borderColor, $tooltipCfg.opacity)}`};
    border-radius: ${({ $tooltipCfg }: StyledTooltipProps) => $tooltipCfg.borderRadius};
    padding: ${({ $tooltipCfg }: StyledTooltipProps) => $tooltipCfg.padding};
    max-width: ${({ $tooltipCfg }: StyledTooltipProps) => $tooltipCfg.maxWidth};
  }
  & .arrow {
    color: ${({ $tooltipCfg }: StyledTooltipProps) => $tooltipCfg.color};
    &::before {
      background-color: ${({ $tooltipCfg }: StyledTooltipProps) => $tooltipCfg.backgroundColor};
      border: ${({ $tooltipCfg }: StyledTooltipProps) =>
        `${$tooltipCfg.borderWidth} ${$tooltipCfg.borderStyle} ${$tooltipCfg.borderColor}`};
    }
  }
`;

const StyledTooltipHeader = styled.div`
  font-weight: ${(props: any) => props.headerFontWeight};
  font-size: ${(props: any) => props.headerFontSize};
  margin-bottom: ${(props: any) => props.headerMarginBottom};
  letter-spacing: ${(props: any) => props.letterSpacing}px;
  line-height: ${(props: any) => `${Number(String(props.headerFontSize).replace('px', '')) * 1.25}px`};
`;

const StyledTooltipContent = styled.div`
  font-weight: ${(props: any) => props.contentFontWeight};
  font-size: ${(props: any) => props.contentFontSize};
  line-height: ${(props: any) => `${Number(props.contentFontSize.replace('px', '')) * 1.25}px`};
  letter-spacing: ${(props: any) => props.letterSpacing}px;
`;

const StyledAutocompleteOption = styled.div<{ selected: number }>`
  display: flex;
  align-items: flex-start;
  width: 100%;
  margin: 0;
  font-size: 15px;
  color: #33485e;
  background: ${({ selected }) => (selected ? '#eaf2ff' : '#ffffff')};
  border: 1px solid transparent;
  &:hover {
    background: ${({ selected }) => (selected ? '#eaf2ff' : '#f6f2ff')};
  }
  &.with-checkbox {
    display: flex;
    align-items: flex-start;
  }
`;

const OptionLabel = styled('span')`
  flex: 1;
  display: block;
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  word-break: normal;
  line-height: 1.1;
  margin-top: 0;
`;

const SelectedValuesSummary = styled('span')`
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 15px;
  font-weight: 400;
  color: #33485e;
  padding: 5px 4px !important;
  margin-left: 12px;
`;

function TooltipTitle(name: any, description: any, tooltipCfg: any) {
  return (
    <>
      <StyledTooltipHeader {...tooltipCfg}>{name}</StyledTooltipHeader>
      <StyledTooltipContent {...tooltipCfg}>{description}</StyledTooltipContent>
    </>
  );
}

const optionKey = (option: any) => option?.id ?? option?.value ?? option?.iso ?? option?.name ?? option;

const DropDownFilter = ({
  name,
  value,
  onChange,
  onChangeTimeResolution,
  isTimeResolution,
  isTimeResolutionLongShort,
  options,
  description,
  tooltipCfg,
  timeResolution,
  multiSelect = false,
}: FilterItemProps) => {
  const normalizedTooltipCfg = useMemo(() => normalizeTooltipCfg(tooltipCfg), [tooltipCfg]);
  const enableMultiSelect = useMemo(
    () => multiSelect === true || (typeof multiSelect === 'string' && multiSelect === 'true'),
    [multiSelect],
  );
  const isNarrativeFilter = useMemo(
    () => typeof name === 'string' && name.trim().toLowerCase() === 'narrative',
    [name],
  );
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [showLimitAlert, setShowLimitAlert] = useState(false);
  const limitAlertTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleOnOpen = useCallback(() => setIsOpen(true), []);
  const handleOnClose = useCallback(() => setIsOpen(false), []);

  const availableOptions = useMemo(() => options || [], [options]);

  const toOption = useCallback(
    (optionValue: string) => {
      if (!optionValue) return null;
      if (!availableOptions.length) {
        return { id: optionValue, name: optionValue };
      }
      const matchedOption = availableOptions.find(
        opt => optionKey(opt) === optionValue || opt?.name === optionValue || opt?.id === optionValue,
      );
      if (matchedOption) return matchedOption;
      return { id: optionValue, name: optionValue };
    },
    [availableOptions],
  );

  const selectedOption = useMemo(() => {
    if (enableMultiSelect) return null;
    if (typeof value !== 'string') return null;
    return toOption(value);
  }, [enableMultiSelect, toOption, value]);

  const normalizedMultiValues = useMemo(() => {
    if (!enableMultiSelect) return [];
    let candidate: string[] = [];
    if (Array.isArray(value)) {
      candidate = value;
    } else if (typeof value === 'string' && value) {
      candidate = value
        .split(',')
        .map(v => v.trim())
        .filter(Boolean);
    }
    if (!isNarrativeFilter) {
      return candidate;
    }
    return candidate.slice(0, MAX_MULTI_SELECT_OPTIONS);
  }, [enableMultiSelect, isNarrativeFilter, value]);

  const selectedOptions = useMemo(() => {
    if (!enableMultiSelect) return [];
    const values = isNarrativeFilter ? normalizedMultiValues.slice(0, MAX_MULTI_SELECT_OPTIONS) : normalizedMultiValues;
    return values.map(optionValue => toOption(optionValue)).filter(Boolean) as Array<{
      id: string;
      name: string;
    }>;
  }, [enableMultiSelect, isNarrativeFilter, normalizedMultiValues, toOption]);

  const [localValue, setLocalValue] = useState(selectedOptions);

  useEffect(() => {
    if (!enableMultiSelect) return;
    setLocalValue(prev => {
      const prevKeys = prev.map(optionKey).join('|');
      const nextKeys = selectedOptions.map(optionKey).join('|');
      if (prevKeys === nextKeys) return prev;
      return selectedOptions;
    });
  }, [enableMultiSelect, selectedOptions]);

  useEffect(() => {
    if (!showLimitAlert) {
      return;
    }
    if (limitAlertTimerRef.current) {
      clearTimeout(limitAlertTimerRef.current);
    }
    limitAlertTimerRef.current = setTimeout(() => {
      setShowLimitAlert(false);
      limitAlertTimerRef.current = null;
    }, 4000);
    return () => {
      if (limitAlertTimerRef.current) {
        clearTimeout(limitAlertTimerRef.current);
        limitAlertTimerRef.current = null;
      }
    };
  }, [showLimitAlert]);

  useEffect(() => {
    return () => {
      if (limitAlertTimerRef.current) {
        clearTimeout(limitAlertTimerRef.current);
        limitAlertTimerRef.current = null;
      }
    };
  }, []);

  const resolvedValue = useMemo(() => {
    if (enableMultiSelect) {
      return localValue;
    }
    if (selectedOption) {
      return selectedOption;
    }
    if (typeof value === 'string' && value) {
      return { id: value, name: value };
    }
    return null;
  }, [enableMultiSelect, localValue, selectedOption, value]);

  const resolvedValueKey = useMemo(() => {
    if (enableMultiSelect) return null;
    return resolvedValue ? optionKey(resolvedValue) : null;
  }, [enableMultiSelect, resolvedValue]);

  const multiValueKeys = useMemo(() => {
    if (!enableMultiSelect) return new Set<string>();
    return new Set(localValue.map(optionKey));
  }, [enableMultiSelect, localValue]);

  const selectedSummary = useMemo(() => {
    if (!enableMultiSelect || !localValue.length) return '';
    if (localValue.length === 1) {
      return localValue[0]?.name ?? optionKey(localValue[0]);
    }
    return `${localValue.length} items`;
  }, [enableMultiSelect, localValue]);

  const selectedSummaryTitle = useMemo(() => {
    if (!enableMultiSelect || !localValue.length) return '';
    return localValue.map(opt => opt?.name ?? optionKey(opt)).join(', ');
  }, [enableMultiSelect, localValue]);

  useEffect(() => {
    if (!enableMultiSelect) return;
    if (!isOpen) {
      setSearchValue('');
    }
  }, [enableMultiSelect, isOpen]);

  const formatOptionValue = useCallback((option: any) => optionKey(option), []);

  const handleInputChange = useCallback(
    (_event: any, newInputValue: string, reason: string) => {
      if (!enableMultiSelect) return;
      if (reason === 'reset') {
        setSearchValue('');
        return;
      }
      setSearchValue(newInputValue);
    },
    [enableMultiSelect],
  );

  const menuOffsets = useMemo(() => {
    if (name === 'Time Resolution' || name === 'Formation Period') {
      return { vertical: -20, horizontal: 2 };
    }
    return { vertical: -2, horizontal: 4 };
  }, [name]);

  if ((name === 'Time Resolution' || name === 'Formation Period') && !isTimeResolutionLongShort) return <div />;

  return (
    <>
      <StyledTooltip
        title={TooltipTitle(name, description, normalizedTooltipCfg)}
        aria-label={description}
        disableFocusListener={true}
        disableHoverListener={description === undefined || description === ''}
        arrow
        placement="left"
        enterDelay={500}
        $tooltipCfg={normalizedTooltipCfg}
      >
        <SelectContainer variant="filled" className="tooltip-wrapper">
          <StyledAutocomplete
            options={availableOptions}
            getOptionLabel={(option: any) => option?.name || ''}
            popupIcon={<ArrowDown />}
            isopen={isOpen ? 1 : 0}
            disableClearable
            blurOnSelect={!enableMultiSelect}
            disableCloseOnSelect={enableMultiSelect}
            id={`auto-complete-${name}-${String(value)}`}
            autoComplete
            selectOnFocus={false}
            onOpen={handleOnOpen}
            onClose={(event: any, reason: any) => {
              handleOnClose();
              if (!enableMultiSelect) return;
              const formattedValue = localValue.map(formatOptionValue);
              const originalValue = normalizedMultiValues;
              const trimmedValue = isNarrativeFilter
                ? formattedValue.slice(0, MAX_MULTI_SELECT_OPTIONS)
                : formattedValue;
              if (trimmedValue.join(',') !== originalValue.join(',')) {
                onChange(trimmedValue);
              }
            }}
            includeInputInList
            value={resolvedValue}
            multiple={enableMultiSelect}
            inputValue={enableMultiSelect ? searchValue : undefined}
            onInputChange={enableMultiSelect ? handleInputChange : undefined}
            renderTags={
              enableMultiSelect
                ? selected => {
                    if (!selected?.length || searchValue) return [];
                    const summary = selectedSummary;
                    if (!summary) return [];
                    return [
                      <SelectedValuesSummary key="selected-summary" title={selectedSummaryTitle || summary}>
                        {summary}
                      </SelectedValuesSummary>,
                    ];
                  }
                : undefined
            }
            isOptionEqualToValue={(option: any, optionValue: any) => optionKey(option) === optionKey(optionValue)}
            renderInput={(params: any) => (
              <TextField
                label={name}
                {...params}
                margin="normal"
                style={{ margin: '0px', border: 'none' }}
                InputProps={{
                  ...params.InputProps,
                  sx: enableMultiSelect
                    ? {
                        paddingLeft: '12px',
                      }
                    : params.InputProps?.sx,
                }}
              />
            )}
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [menuOffsets.horizontal, menuOffsets.vertical],
                    },
                  },
                ],
              },
              paper: {
                sx: {
                  backgroundColor: 'var(--background-chart)',
                  border: `${DROPDOWN_BORDER_WIDTH} solid ${DROPDOWN_BORDER_COLOR_ACTIVE}`,
                  borderTop: 'none',
                  borderRadius: '0 0 5px 5px',
                  boxShadow: 'none',
                  marginTop: 0,
                  '& .MuiAutocomplete-option': {
                    alignItems: 'flex-start',
                    padding: '0 8px !important',
                    minHeight: '0px !important',
                  },
                },
              },
            }}
            ListboxProps={{
              style: {
                padding: 0,
              },
            }}
            renderOption={(props: any, option: any, state: any) => {
              const optionSelected = enableMultiSelect
                ? multiValueKeys.has(optionKey(option))
                : Boolean(state?.selected);

              return (
                <li {...props} key={optionKey(option)}>
                  <StyledTooltip
                    title={TooltipTitle(option?.name, option?.description, normalizedTooltipCfg)}
                    aria-label={option?.description}
                    disableHoverListener={option?.description === undefined || option?.description === ''}
                    arrow={true}
                    placement="left"
                    enterDelay={500}
                    $tooltipCfg={normalizedTooltipCfg}
                  >
                    <StyledAutocompleteOption
                      className={enableMultiSelect ? 'with-checkbox' : undefined}
                      selected={optionSelected ? 1 : 0}
                    >
                      {enableMultiSelect && (
                        <Checkbox
                          sx={{
                            marginLeft: '-4px',
                            p: 0,
                            pr: '8px',
                            alignSelf: 'flex-start',
                            mt: 0,
                          }}
                          checked={multiValueKeys.has(optionKey(option))}
                          tabIndex={-1}
                          disableRipple
                          size="small"
                        />
                      )}
                      <OptionLabel>{option?.name}</OptionLabel>
                    </StyledAutocompleteOption>
                  </StyledTooltip>
                </li>
              );
            }}
            onChange={(event: any, newValue: any) => {
              if (enableMultiSelect) {
                if (!Array.isArray(newValue)) {
                  setLocalValue([]);
                  return;
                }
                if (isNarrativeFilter && newValue.length > MAX_MULTI_SELECT_OPTIONS) {
                  setShowLimitAlert(true);
                  setLocalValue(prev => prev);
                  return;
                }
                setLocalValue(newValue);
                return;
              }
              if (!newValue) return;
              const nextValue = optionKey(newValue);
              onChange(nextValue);
            }}
          />
        </SelectContainer>
      </StyledTooltip>
      {isNarrativeFilter && showLimitAlert && (
        <AlertBar
          message={`You can select up to ${MAX_MULTI_SELECT_OPTIONS} narratives.`}
          autoHideDuration={4000}
          onClose={() => setShowLimitAlert(false)}
        />
      )}
    </>
  );
};

export default DropDownFilter;
